import { useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { Check, ExternalLink, Mail, Send, Sparkles, X } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { submitVisitorLead } from "@/lib/marketplace.functions";
import { toast } from "sonner";
import { useHydratedTranslation } from "@/components/marketing/Layout";

// Shared premium chat surface for every twin touchpoint: marketplace,
// embed widget and the owner dashboard. Renders markdown, knowledge-base
// citations, AI-suggested follow-ups and (visitor mode) in-chat lead capture.

const FOLLOWUP_MARKER = "⋄";

export type Source = { n: number; title: string; url: string | null };

/** Extract the numbered knowledge sources attached by /api/chat as metadata. */
export function messageSources(metadata: unknown): Source[] {
  const s = (metadata as any)?.sources;
  return Array.isArray(s) ? (s as Source[]) : [];
}

export interface TwinChatProps {
  companyId: string;
  companyName: string;
  mode: "visitor" | "owner";
  /** Stable id so the server threads + persists visitor turns. */
  conversationId?: string;
  /** Owner mode: called after each completed exchange (for logTurn). */
  onTurn?: (userText: string, assistantText: string) => void;
  starterQuestions?: string[];
  placeholder?: string;
  /** Extra class on the scrollable message area. */
  bodyClassName?: string;
}

/** Split an assistant message into visible text and follow-up suggestions. */
export function splitFollowups(raw: string): { text: string; followups: string[] } {
  const idx = raw.indexOf(FOLLOWUP_MARKER);
  if (idx === -1) return { text: raw, followups: [] };
  const text = raw.slice(0, idx).trimEnd();
  const tail = raw.slice(idx);
  const m = tail.match(/FOLLOWUPS:\s*([\s\S]*)/);
  const followups = m
    ? m[1]
        .split("|")
        .map((s) => s.trim().replace(/^[-–•]\s*/, ""))
        .filter((s) => s.length > 2 && s.length < 120)
        .slice(0, 3)
    : [];
  return { text, followups };
}

/** Minimal inline markdown: **bold**, *italic*, `code`, [text](url), [n] citations.
 * Theme-neutral: colors derive from the surrounding foreground color. */
function renderInline(text: string, sources: Source[], keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const re =
    /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(\[([^\]]+)\]\((https?:\/\/[^)]+)\))|(\[(\d)\])/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const key = `${keyPrefix}-${i++}`;
    if (m[2] != null) {
      nodes.push(<strong key={key}>{m[2]}</strong>);
    } else if (m[4] != null) {
      nodes.push(<em key={key}>{m[4]}</em>);
    } else if (m[6] != null) {
      nodes.push(
        <code key={key} className="px-1 py-0.5 rounded bg-current/10 text-[0.85em] font-mono">
          {m[6]}
        </code>,
      );
    } else if (m[8] != null && m[9] != null) {
      nodes.push(
        <a
          key={key}
          href={m[9]}
          target="_blank"
          rel="noreferrer noopener"
          className="underline underline-offset-2 opacity-90 hover:opacity-100"
        >
          {m[8]}
        </a>,
      );
    } else if (m[11] != null) {
      const n = Number(m[11]);
      const src = sources.find((s) => s.n === n);
      nodes.push(
        <sup key={key}>
          <span
            title={src ? src.title : `Source ${n}`}
            className="inline-flex items-center justify-center min-w-4 h-4 px-1 mx-0.5 rounded-full bg-current/15 text-[10px] font-semibold align-super cursor-default"
          >
            {n}
          </span>
        </sup>,
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** Line-based markdown block renderer (paragraphs, bullet/numbered lists). */
export function Markdown({ text, sources }: { text: string; sources: Source[] }) {
  const blocks = useMemo(() => {
    const lines = text.replace(/\r\n/g, "\n").split("\n");
    const out: { type: "p" | "ul" | "ol"; lines: string[] }[] = [];
    for (const line of lines) {
      const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
      const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
      if (bullet) {
        if (out[out.length - 1]?.type !== "ul") out.push({ type: "ul", lines: [] });
        out[out.length - 1].lines.push(bullet[1]);
      } else if (numbered) {
        if (out[out.length - 1]?.type !== "ol") out.push({ type: "ol", lines: [] });
        out[out.length - 1].lines.push(numbered[1]);
      } else if (line.trim() === "") {
        if (out[out.length - 1]?.type === "p" && out[out.length - 1].lines.length > 0) {
          out.push({ type: "p", lines: [] });
        }
      } else {
        const clean = line.replace(/^#{1,4}\s+/, "");
        if (out[out.length - 1]?.type !== "p") out.push({ type: "p", lines: [] });
        out[out.length - 1].lines.push(clean);
      }
    }
    return out.filter((b) => b.lines.length > 0);
  }, [text]);

  return (
    <div className="space-y-2">
      {blocks.map((b, bi) =>
        b.type === "p" ? (
          <p key={bi} className="leading-relaxed">
            {b.lines.map((l, li) => (
              <span key={li}>
                {renderInline(l, sources, `${bi}-${li}`)}
                {li < b.lines.length - 1 ? " " : null}
              </span>
            ))}
          </p>
        ) : b.type === "ul" ? (
          <ul key={bi} className="space-y-1 pl-1">
            {b.lines.map((l, li) => (
              <li key={li} className="flex gap-2">
                <span className="mt-[0.55em] h-1 w-1 rounded-full bg-current opacity-70 shrink-0" />
                <span className="leading-relaxed">{renderInline(l, sources, `${bi}-${li}`)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <ol key={bi} className="space-y-1 pl-1">
            {b.lines.map((l, li) => (
              <li key={li} className="flex gap-2">
                <span className="text-xs mt-[0.3em] font-mono opacity-70 shrink-0">{li + 1}.</span>
                <span className="leading-relaxed">{renderInline(l, sources, `${bi}-${li}`)}</span>
              </li>
            ))}
          </ol>
        ),
      )}
    </div>
  );
}

export function SourceChips({ text, sources }: { text: string; sources: Source[] }) {
  const cited = useMemo(() => {
    const ns = new Set<number>();
    for (const m of text.matchAll(/\[(\d)\]/g)) ns.add(Number(m[1]));
    const seen = new Set<string>();
    return sources.filter((s) => {
      if (!ns.has(s.n) || seen.has(s.title + (s.url ?? ""))) return false;
      seen.add(s.title + (s.url ?? ""));
      return true;
    });
  }, [text, sources]);
  if (cited.length === 0) return null;
  return (
    <div className="mt-2.5 pt-2 border-t border-current/15 flex flex-wrap gap-1.5">
      {cited.map((s) =>
        s.url ? (
          <a
            key={s.n}
            href={s.url}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-current/5 border border-current/20 opacity-80 hover:opacity-100 transition max-w-48"
          >
            <span className="font-semibold">{s.n}</span>
            <span className="truncate">{s.title}</span>
            <ExternalLink className="h-2.5 w-2.5 shrink-0" />
          </a>
        ) : (
          <span
            key={s.n}
            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-current/5 border border-current/20 opacity-80 max-w-48"
          >
            <span className="font-semibold">{s.n}</span>
            <span className="truncate">{s.title}</span>
          </span>
        ),
      )}
    </div>
  );
}

const INTENT_RE =
  /(price|pricing|cost|quote|buy|purchase|contract|demo|trial|partner|invest|offer|preis|kosten|angebot|kaufen|vertrag|comprar|precio|acheter|prix)/i;

function InlineLeadCard({
  companyId,
  companyName,
  conversationId,
  onDone,
  onDismiss,
}: {
  companyId: string;
  companyName: string;
  conversationId?: string;
  onDone: () => void;
  onDismiss: () => void;
}) {
  const t = useHydratedTranslation();
  const sendLead = useServerFn(submitVisitorLead);
  const [f, setF] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    postalCode: "",
    city: "",
    country: "",
  });
  const [showAddress, setShowAddress] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  const fieldCls =
    "flex-1 rounded-lg bg-white/5 border border-border px-2.5 py-1.5 text-xs focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) return;
    setBusy(true);
    try {
      await sendLead({
        data: {
          companyId,
          conversationId,
          name: f.name,
          email: f.email,
          phone: f.phone || undefined,
          company: f.company || undefined,
          address: f.address || undefined,
          city: f.city || undefined,
          postalCode: f.postalCode || undefined,
          country: f.country || undefined,
        },
      });
      toast.success(t("app.pages.twinChat.willGetBack", { company: companyName }));
      onDone();
    } catch (err: any) {
      toast.error(err?.message ?? t("app.pages.twinChat.couldNotSend"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex justify-start">
      <form
        onSubmit={submit}
        className="w-full max-w-[92%] rounded-2xl border border-[oklch(0.82_0.18_168_/_0.35)] bg-[oklch(0.82_0.18_168_/_0.06)] px-4 py-3 space-y-2 relative"
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t("app.pages.twinChat.dismiss")}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <div className="flex items-center gap-1.5 text-xs font-medium text-[oklch(0.82_0.18_168)]">
          <Mail className="h-3.5 w-3.5" />{" "}
          {t("app.pages.twinChat.followUpQ", { company: companyName })}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={f.name}
            onChange={set("name")}
            aria-label={t("app.pages.twinChat.yourName")}
            placeholder={t("app.pages.twinChat.yourName")}
            required
            maxLength={120}
            className={fieldCls}
          />
          <input
            value={f.email}
            onChange={set("email")}
            aria-label={t("app.pages.twinChat.workEmail")}
            type="email"
            placeholder={t("app.pages.twinChat.workEmail")}
            required
            maxLength={200}
            className={fieldCls}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={f.phone}
            onChange={set("phone")}
            aria-label={t("app.pages.twinChat.phone", { defaultValue: "Phone" })}
            placeholder={t("app.pages.twinChat.phone", { defaultValue: "Phone" })}
            maxLength={60}
            className={fieldCls}
          />
          <input
            value={f.company}
            onChange={set("company")}
            aria-label={t("app.pages.twinChat.company", { defaultValue: "Company" })}
            placeholder={t("app.pages.twinChat.company", { defaultValue: "Company" })}
            maxLength={200}
            className={fieldCls}
          />
        </div>

        {showAddress ? (
          <div className="space-y-2">
            <input
              value={f.address}
              onChange={set("address")}
              aria-label={t("app.pages.twinChat.address", { defaultValue: "Street & number" })}
              placeholder={t("app.pages.twinChat.address", { defaultValue: "Street & number" })}
              maxLength={400}
              className="w-full rounded-lg bg-white/5 border border-border px-2.5 py-1.5 text-xs focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
            />
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                value={f.postalCode}
                onChange={set("postalCode")}
                aria-label={t("app.pages.twinChat.postal", { defaultValue: "ZIP" })}
                placeholder={t("app.pages.twinChat.postal", { defaultValue: "ZIP" })}
                maxLength={40}
                className="sm:w-24 rounded-lg bg-white/5 border border-border px-2.5 py-1.5 text-xs focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
              />
              <input
                value={f.city}
                onChange={set("city")}
                aria-label={t("app.pages.twinChat.city", { defaultValue: "City" })}
                placeholder={t("app.pages.twinChat.city", { defaultValue: "City" })}
                maxLength={120}
                className={fieldCls}
              />
              <input
                value={f.country}
                onChange={set("country")}
                aria-label={t("app.pages.twinChat.country", { defaultValue: "Country" })}
                placeholder={t("app.pages.twinChat.country", { defaultValue: "Country" })}
                maxLength={120}
                className={fieldCls}
              />
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowAddress(true)}
            className="text-[11px] text-[oklch(0.82_0.18_168)] hover:underline"
          >
            {t("app.pages.twinChat.addAddress", { defaultValue: "+ Add full address" })}
          </button>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={busy || !f.name.trim() || !f.email.trim()}
            className="rounded-lg brand-gradient text-black px-4 py-1.5 text-xs font-medium disabled:opacity-50"
          >
            {busy ? "…" : t("app.pages.twinChat.send")}
          </button>
        </div>
      </form>
    </div>
  );
}

export function TwinChat({
  companyId,
  companyName,
  mode,
  conversationId,
  onTurn,
  starterQuestions = ["What do you do?", "Who is your ideal customer?", "How can we partner?"],
  placeholder,
  bodyClassName = "",
}: TwinChatProps) {
  const t = useHydratedTranslation();
  const lastUserRef = useRef("");
  const { messages, sendMessage, status } = useChat({
    transport: new DefaultChatTransport({ api: "/api/chat" }),
    onFinish: ({ message }) => {
      if (!onTurn) return;
      const raw = message.parts.map((p: any) => (p.type === "text" ? p.text : "")).join("");
      const { text } = splitFollowups(raw);
      if (lastUserRef.current && text) onTurn(lastUserRef.current, text);
    },
  });
  const [input, setInput] = useState("");
  const [leadState, setLeadState] = useState<"idle" | "shown" | "done" | "dismissed">("idle");
  const scrollRef = useRef<HTMLDivElement>(null);
  const busy = status === "submitted" || status === "streaming";

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, leadState]);

  // Offer in-chat contact capture when the visitor signals intent, or after
  // a few exchanges. Shown once per session, dismissible.
  const assistantCount = messages.filter((m) => m.role === "assistant").length;
  useEffect(() => {
    if (mode !== "visitor" || leadState !== "idle" || busy) return;
    const intent = INTENT_RE.test(lastUserRef.current);
    if ((intent && assistantCount >= 1) || assistantCount >= 3) setLeadState("shown");
  }, [assistantCount, busy, leadState, mode]);

  function ask(text: string) {
    lastUserRef.current = text;
    sendMessage({ text }, { body: { companyId, conversationId } });
  }
  function submit(e: React.FormEvent) {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    ask(text);
    setInput("");
  }

  const lastAssistantId = [...messages].reverse().find((m) => m.role === "assistant")?.id;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div ref={scrollRef} className={`flex-1 overflow-y-auto p-5 space-y-4 ${bodyClassName}`}>
        {messages.length === 0 && (
          <div className="text-center py-10">
            <Sparkles className="h-7 w-7 mx-auto text-[oklch(0.82_0.18_168)]" />
            <p className="mt-3 text-sm text-muted-foreground">Ask {companyName} anything</p>
            <div className="mt-5 flex flex-wrap gap-2 justify-center">
              {starterQuestions.map((s) => (
                <button
                  key={s}
                  onClick={() => ask(s)}
                  className="glass text-xs px-3 py-1.5 rounded-full border border-border hover:bg-white/5"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => {
          const raw = m.parts.map((p: any) => (p.type === "text" ? p.text : "")).join("");
          const isUser = m.role === "user";
          if (isUser) {
            return (
              <div key={m.id} className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap brand-gradient text-black">
                  {raw}
                </div>
              </div>
            );
          }
          const { text, followups } = splitFollowups(raw);
          const sources: Source[] = Array.isArray((m.metadata as any)?.sources)
            ? ((m.metadata as any).sources as Source[])
            : [];
          const isLast = m.id === lastAssistantId;
          return (
            <div key={m.id} className="space-y-2">
              <div className="flex justify-start">
                <div className="h-8 w-8 rounded-lg brand-gradient flex items-center justify-center text-black font-bold text-xs mr-2 shrink-0">
                  {companyName[0]}
                </div>
                <div className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm glass">
                  <Markdown text={text} sources={sources} />
                  <SourceChips text={text} sources={sources} />
                </div>
              </div>
              {isLast && !busy && followups.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pl-10">
                  {followups.map((f) => (
                    <button
                      key={f}
                      onClick={() => ask(f)}
                      className="text-xs px-3 py-1.5 rounded-full border border-[oklch(0.82_0.18_168_/_0.3)] text-[oklch(0.82_0.18_168)] hover:bg-[oklch(0.82_0.18_168_/_0.08)] transition"
                    >
                      {f}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {mode === "visitor" && leadState === "shown" && !busy && (
          <InlineLeadCard
            companyId={companyId}
            companyName={companyName}
            conversationId={conversationId}
            onDone={() => setLeadState("done")}
            onDismiss={() => setLeadState("dismissed")}
          />
        )}
        {leadState === "done" && (
          <div className="flex justify-start pl-10">
            <div className="inline-flex items-center gap-1.5 text-xs text-[oklch(0.82_0.18_168)]">
              <Check className="h-3.5 w-3.5" />{" "}
              {t("app.pages.twinChat.contactShared", { company: companyName })}
            </div>
          </div>
        )}
        {busy && messages[messages.length - 1]?.role === "user" && (
          <div className="flex justify-start pl-10">
            <div className="glass rounded-2xl px-3.5 py-2.5">
              <span className="inline-flex gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[oklch(0.82_0.18_168)] animate-bounce" />
                <span className="h-1.5 w-1.5 rounded-full bg-[oklch(0.82_0.18_168)] animate-bounce [animation-delay:120ms]" />
                <span className="h-1.5 w-1.5 rounded-full bg-[oklch(0.82_0.18_168)] animate-bounce [animation-delay:240ms]" />
              </span>
            </div>
          </div>
        )}
      </div>
      <form onSubmit={submit} className="border-t border-border p-4 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          aria-label={t("app.pages.twinChat.askPlaceholder", { company: companyName })}
          placeholder={
            placeholder ?? t("app.pages.twinChat.askPlaceholder", { company: companyName })
          }
          className="flex-1 rounded-full bg-white/5 border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-[oklch(0.82_0.18_168_/_0.6)]"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          className="rounded-full brand-gradient text-black px-5 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" /> {t("app.pages.twinChat.send")}
        </button>
      </form>
    </div>
  );
}
